/**
 * Spell area («зона заклинания») — the shape + size a spell carries and the pure geometry of fitting it
 * from a caster's token. Used by the sheet (data), the battle HUD (aim icon) and the canvas (fit mode).
 * No DOM, no side effects.
 */
import type { AoeShape } from './utils.js';

/** The area of a spell. `sizeFt`: radius (sphere/cylinder), length (cone/line), side (cube). The line is 5 ft wide in the engine. */
export interface SpellAoe {
  shape: AoeShape;
  sizeFt: number;
  /** Custom spells only: how far from the caster the area may be centered (0 = self). An SRD spell's reach comes from its range text. */
  rangeFt?: number;
}

/** The SRD `area_of_effect.type` words → engine shapes. */
export const SRD_AOE_TYPE_TO_SHAPE: Readonly<Record<string, AoeShape>> = {
  sphere: 'sphere',
  cylinder: 'cylinder',
  cone: 'cone',
  cube: 'cube',
  line: 'line',
};

/** SRD `area_of_effect` → a spell area; null when the type is unknown or the size is not a positive number. */
export function mapSrdAoe(raw: { type?: string; size?: number } | null | undefined): SpellAoe | null {
  if (!raw || typeof raw.type !== 'string') return null;
  const shape = SRD_AOE_TYPE_TO_SHAPE[raw.type];
  if (!shape) return null;
  if (typeof raw.size !== 'number' || !Number.isFinite(raw.size) || raw.size <= 0) return null;
  return { shape, sizeFt: raw.size };
}

/** A user-entered area: shape known, size 1..300 ft (what `template:place` accepts). */
export function isValidSpellAoe(v: unknown): v is SpellAoe {
  if (!v || typeof v !== 'object') return false;
  const a = v as Partial<SpellAoe>;
  return typeof a.shape === 'string'
    && Object.values(SRD_AOE_TYPE_TO_SHAPE).includes(a.shape as AoeShape)
    && typeof a.sizeFt === 'number' && Number.isFinite(a.sizeFt) && a.sizeFt >= 1 && a.sizeFt <= 300
    && (a.rangeFt === undefined || (typeof a.rangeFt === 'number' && Number.isFinite(a.rangeFt) && a.rangeFt >= 0 && a.rangeFt <= 100000));
}

/**
 * How far from the caster the spell reaches, in feet: 0 — «Self» (the area starts at the caster), a number — the range,
 * null — no limit we can measure («Sight», «Unlimited», a spell without a range text).
 */
export function parseSpellRangeFt(range: string | undefined | null): number | null {
  if (!range) return null;
  const r = range.trim().toLowerCase();
  if (r.startsWith('self')) return 0;
  if (r.startsWith('touch')) return 5;
  const mile = /(\d+(?:\.\d+)?)\s*mile/.exec(r);
  if (mile) return Math.round(Number(mile[1]) * 5280);
  const feet = /(\d+(?:\.\d+)?)\s*(?:feet|foot|ft|фут|фт)/.exec(r);
  if (feet) return Number(feet[1]);
  return null;
}

/**
 * How an area is aimed (5e): only a spell with the range «Self» starts at the caster. A sphere / cylinder with a range is
 * centred on a point within it. A cone / line / cube with a range (Web, Wall of Fire…) starts at a point within it and is
 * turned there. A cone / line / cube with no range known starts at the caster (nothing says otherwise).
 *   self       — everything starts at the caster; a cone / line / cube turns to the aim
 *   point      — sphere / cylinder: centre = aim
 *   point-turn — cone / line / cube: origin = the press, turn = the drag after it
 *   from-token — cone / line / cube, range unknown: origin = caster, turn to the aim
 */
export type SpellAoeAimMode = 'self' | 'point' | 'point-turn' | 'from-token';

export function spellAoeAimMode(shape: AoeShape, rangeFt: number | null): SpellAoeAimMode {
  if (rangeFt === 0) return 'self';
  if (shape === 'sphere' || shape === 'cylinder') return 'point';
  return rangeFt === null ? 'from-token' : 'point-turn';
}

export interface SpellAoeFitInput {
  aoe: SpellAoe;
  /** 0 = Self, null = unknown / unlimited (see parseSpellRangeFt). */
  rangeFt: number | null;
  /** Center of the caster's token, canvas px. */
  tokenX: number;
  tokenY: number;
  /** Where the aim is (cursor / finger), canvas px: the centre / origin of a point area, the direction of the others. */
  aimX: number;
  aimY: number;
  /** point-turn only: where the area is turned to (after the origin is set). */
  turnX?: number;
  turnY?: number;
  gridSize: number;
  gridSizeFt: number;
}

export interface SpellAoeFit {
  shape: AoeShape;
  originX: number;
  originY: number;
  sizeFt: number;
  rotation: number;
  /** A point-placed area whose origin is farther than the spell's range from the caster. */
  outOfRange: boolean;
}

/** Centre of the grid cell the point is IN (not round(): that jumps to the next cell from the right / lower half of a cell). */
export const cellCenter = (v: number, gridSize: number): number => Math.floor(v / gridSize) * gridSize + gridSize / 2;

const angleDeg = (dx: number, dy: number): number => (dx === 0 && dy === 0 ? 0 : ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360);

export function fitSpellAoe(i: SpellAoeFitInput): SpellAoeFit {
  const { aoe } = i;
  const mode = spellAoeAimMode(aoe.shape, i.rangeFt);
  if (mode === 'self' && (aoe.shape === 'sphere' || aoe.shape === 'cylinder')) {
    return { shape: aoe.shape, originX: i.tokenX, originY: i.tokenY, sizeFt: aoe.sizeFt, rotation: 0, outOfRange: false };
  }
  if (mode === 'point' || mode === 'point-turn') {
    const distFt = (Math.hypot(i.aimX - i.tokenX, i.aimY - i.tokenY) / i.gridSize) * i.gridSizeFt;
    const outOfRange = i.rangeFt !== null && distFt > i.rangeFt + 1e-6;
    const rotation = mode === 'point-turn' && i.turnX !== undefined && i.turnY !== undefined ? angleDeg(i.turnX - i.aimX, i.turnY - i.aimY) : 0;
    return { shape: aoe.shape, originX: i.aimX, originY: i.aimY, sizeFt: aoe.sizeFt, rotation, outOfRange };
  }
  return { shape: aoe.shape, originX: i.tokenX, originY: i.tokenY, sizeFt: aoe.sizeFt, rotation: angleDeg(i.aimX - i.tokenX, i.aimY - i.tokenY), outOfRange: false };
}

/** The aim icon is offered only for an area `template:place` can carry (a 40 000-ft «Forbiddance» cube is not a thing on a map). */
export const canFitSpellAoe = isValidSpellAoe;
